/**
 * useRouteClimbHistory Hook
 *
 * Custom hook for querying a user's climb history for a specific route.
 * Returns logs sorted by loggedAt (newest first) with real-time updates.
 */

import { useState, useEffect, useMemo } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import { getFirebaseDb } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';
import type { ClimbLog, ClimbOutcome } from '@flash/shared';

/**
 * Convert Firestore document to ClimbLog type
 */
const docToClimbLog = (docData: DocumentData, docId: string): ClimbLog => {
  return {
    id: docId,
    userId: docData['userId'] as string,
    routeId: docData['routeId'] as string,
    gymId: docData['gymId'] as string,
    outcome: docData['outcome'] as ClimbOutcome,
    notes: (docData['notes'] as string | null) ?? null,
    loggedAt: docData['loggedAt'],
    createdAt: docData['createdAt'],
  };
};

/**
 * State interface for route climb history query
 */
interface UseRouteClimbHistoryState {
  /** List of climb logs for this route */
  logs: ClimbLog[];
  /** Whether data is being loaded */
  loading: boolean;
  /** Error message if query failed */
  error: string | null;
}

/**
 * Return type for useRouteClimbHistory hook
 */
interface UseRouteClimbHistoryResult extends UseRouteClimbHistoryState {
  /** Whether user has ever sent this route */
  hasSent: boolean;
  /** Total sends on this route by user */
  sendsCount: number;
  /** Total attempts on this route by user */
  attemptsCount: number;
  /** Total logs on this route by user */
  totalCount: number;
  /** Date of most recent send (null if no sends) */
  lastSendDate: Date | null;
  /** Date of most recent attempt (null if no attempts) */
  lastAttemptDate: Date | null;
}

/**
 * Hook to fetch climb history for a specific route by the current user
 *
 * @param routeId - The route ID to fetch history for
 * @returns Object containing climb logs, loading state, error, and computed stats
 *
 * @example
 * ```tsx
 * const {
 *   logs,
 *   loading,
 *   error,
 *   hasSent,
 *   sendsCount,
 *   attemptsCount,
 *   lastSendDate,
 * } = useRouteClimbHistory(routeId);
 *
 * if (loading) return <LoadingSpinner />;
 * if (error) return <ErrorMessage message={error} />;
 *
 * return (
 *   <div>
 *     {hasSent && <Badge>Sent!</Badge>}
 *     <p>Sends: {sendsCount}, Attempts: {attemptsCount}</p>
 *     <ClimbHistoryList logs={logs} />
 *   </div>
 * );
 * ```
 */
export const useRouteClimbHistory = (routeId: string | undefined): UseRouteClimbHistoryResult => {
  const { user } = useAuth();

  const [state, setState] = useState<UseRouteClimbHistoryState>({
    logs: [],
    loading: true,
    error: null,
  });

  // Real-time subscription effect
  useEffect(() => {
    if (!user || !routeId) {
      setState({
        logs: [],
        loading: false,
        error: routeId ? null : 'No route ID provided',
      });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    const db = getFirebaseDb();
    const logsRef = collection(db, 'climbLogs');

    // Query for logs by current user on this specific route
    // Secondary sort by createdAt ensures consistent ordering for logs on the same day
    const logsQuery = query(
      logsRef,
      where('userId', '==', user.uid),
      where('routeId', '==', routeId),
      orderBy('loggedAt', 'desc'),
      orderBy('createdAt', 'desc')
    );

    // Set up real-time listener
    const unsubscribe: Unsubscribe = onSnapshot(
      logsQuery,
      (snapshot) => {
        const logs: ClimbLog[] = snapshot.docs.map((docSnapshot) =>
          docToClimbLog(docSnapshot.data(), docSnapshot.id)
        );

        setState({ logs, loading: false, error: null });
      },
      (err) => {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to fetch route climb history';
        console.error('Error in route climb history subscription:', err);
        setState((prev) => ({ ...prev, loading: false, error: errorMessage }));
      }
    );

    return () => {
      unsubscribe();
    };
  }, [user, routeId]);

  /**
   * Total count of logs
   */
  const totalCount = useMemo(() => state.logs.length, [state.logs]);

  /**
   * Count of sends
   */
  const sendsCount = useMemo(
    () => state.logs.filter((log) => log.outcome === 'send').length,
    [state.logs]
  );

  /**
   * Count of attempts
   */
  const attemptsCount = useMemo(
    () => state.logs.filter((log) => log.outcome === 'attempt').length,
    [state.logs]
  );

  /**
   * Whether user has ever sent this route
   */
  const hasSent = useMemo(() => sendsCount > 0, [sendsCount]);

  /**
   * Date of most recent send
   */
  const lastSendDate = useMemo(() => {
    const sends = state.logs.filter((log) => log.outcome === 'send');
    const latestSend = sends[0];
    if (!latestSend) return null;

    // Logs are already sorted by loggedAt desc, so first send is most recent
    return getDateFromTimestamp(latestSend.loggedAt);
  }, [state.logs]);

  /**
   * Date of most recent attempt
   */
  const lastAttemptDate = useMemo(() => {
    const attempts = state.logs.filter((log) => log.outcome === 'attempt');
    const latestAttempt = attempts[0];
    if (!latestAttempt) return null;

    // Logs are already sorted by loggedAt desc, so first attempt is most recent
    return getDateFromTimestamp(latestAttempt.loggedAt);
  }, [state.logs]);

  return {
    ...state,
    hasSent,
    sendsCount,
    attemptsCount,
    totalCount,
    lastSendDate,
    lastAttemptDate,
  };
};

/**
 * Get Date from a timestamp field
 */
const getDateFromTimestamp = (timestamp: unknown): Date | null => {
  if (!timestamp) return null;

  // Handle Firestore Timestamp with toDate method
  if (typeof timestamp === 'object' && timestamp !== null && 'toDate' in timestamp) {
    return (timestamp as { toDate: () => Date }).toDate();
  }

  // Handle Date object
  if (timestamp instanceof Date) {
    return timestamp;
  }

  // Handle timestamp with seconds/nanoseconds
  if (
    typeof timestamp === 'object' &&
    timestamp !== null &&
    'seconds' in timestamp &&
    'nanoseconds' in timestamp
  ) {
    const ts = timestamp as { seconds: number; nanoseconds: number };
    return new Date(ts.seconds * 1000 + ts.nanoseconds / 1000000);
  }

  return null;
};

export default useRouteClimbHistory;
